import { Component, Input } from '@angular/core';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { DataService } from 'src/app/services/data.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DashboardView } from 'src/app/services/query-context/models/dashboard-view.model';
import { SavedDashboardViewHelper } from '../saved-dashboard-view-helper';
import { ISavedDashboardViewComponentOptions } from '../saved-dashboard-view.component';

@Component({
    selector: 'app-saved-dashboard-view-loader',
    templateUrl: './saved-dashboard-view-loader.component.html',
    styleUrls: ['./saved-dashboard-view-loader.component.scss'],
})
export class SavedDashboardViewLoaderComponent {
    @Input() options: ISavedDashboardViewComponentOptions = {};

    selectedDashboardViewId: string | null = null;
    _selectedDashboardView: DashboardView | null = null;
    savedDashboardViews: DashboardView[] = [];

    loading = false;
    error = false;
    cooldown = false;

    constructor(
        private dataService: DataService,
        private notification: NotificationService,
        private logger: LoggerService,
        private dashboardService: DashboardService,
        private chartColorService: ChartColorService
    ) {}

    get selectedDashboardView(): DashboardView | null {
        return this._selectedDashboardView;
    }

    set selectedDashboardView(newDashboardView: DashboardView | null) {
        this._selectedDashboardView = newDashboardView;
        this.selectedDashboardViewId = newDashboardView?.id ?? null;
    }

    ngOnInit(): void {
        this.getNamedDashboardViews();
        this.dashboardService.currentDashboardView$.subscribe(
            (dashboardView) => {
                this.onNewCurrentDashboardView(dashboardView);
            }
        );

        // subscribe to newDashboardViewSaved$ to know when a new dashboard view is saved
        this.dashboardService.newDashboardViewSaved$.subscribe((contextId) => {
            // reload the named contexts
            this.getNamedDashboardViews();
        });

        // subscribe to initialContextRestored$ to know when the initial context is restored and clear the selected context
        this.dashboardService.initialQueryContextRestored$.subscribe(() => {
            this.selectedDashboardView = null;
        });
    }

    async onSelectSavedContext(contextId: string): Promise<void> {
        // reset the chart color service
        this.chartColorService.reset();

        // if contextId is null, restore the initial context
        if (!contextId) {
            this.dashboardService.restoreInitialContext();
            return;
        }

        this.loading = true;
        try {
            await this.dashboardService.loadSavedDashboardView(contextId);
        } catch (error: any) {
            this.loading = false;
            this.notification.error('Error loading context', error.message);
        }
        // this.selectedContextId = contextId;
        this.loading = false;
    }

    private getNamedDashboardViews(): void {
        this.loading = true;
        this.error = false;

        this.dashboardService
            .getNamedDashboardViews()
            .then((contexts) => {
                this.savedDashboardViews = contexts;
                this.loading = false;
            })
            .catch((error) => {
                this.loading = false;
                this.error = true;
                this.notification.error(
                    'Error loading contexts',
                    error.message
                );
                this.logger.error('Error loading named contexts', error);
            });
    }

    onReload(): void {
        this.getNamedDashboardViews();
    }

    onNewCurrentDashboardView(newDashboardView: DashboardView | null): void {
        if (!newDashboardView) {
            this.selectedDashboardView = null;
            return;
        }
        if (this.selectedDashboardView?.id !== newDashboardView.id) {
            // if contextId in savedContexts then set selectedContextId to contextId
            if (
                this.savedDashboardViews.some(
                    (dv) => dv.id === newDashboardView.id
                )
            ) {
                this.selectedDashboardView = newDashboardView;
            } else {
                this.selectedDashboardView = null;
            }
        }
    }

    async onPrevious(): Promise<void> {
        if (this.cooldown || !this.savedDashboardViews.length) return;
        const currentIndex = this.savedDashboardViews.findIndex(
            (view) => view.id === this.selectedDashboardViewId
        );
        if (currentIndex > 0) {
            await this.onSelectSavedContext(
                this.savedDashboardViews[currentIndex - 1].id
            );
            this.startCooldown();
        }
    }

    async onNext(): Promise<void> {
        if (this.cooldown || !this.savedDashboardViews.length) return;
        const currentIndex = this.savedDashboardViews.findIndex(
            (view) => view.id === this.selectedDashboardViewId
        );
        if (currentIndex < this.savedDashboardViews.length - 1) {
            await this.onSelectSavedContext(
                this.savedDashboardViews[currentIndex + 1].id
            );
            this.startCooldown();
        }
    }

    startCooldown(): void {
        this.cooldown = true;
        setTimeout(() => {
            this.cooldown = false;
        }, 1000);
    }
}
